import { supabase } from './supabase';

// Storage bucket names
export const STORAGE_BUCKETS = {
  LOGOS: 'logos',
  FAVICONS: 'favicons',
  LANDING_ASSETS: 'landing-assets',
  OG_IMAGES: 'og-images',
} as const;

// Upload file to Supabase Storage
export async function uploadFile(
  bucket: string,
  path: string,
  file: File,
  options?: {
    cacheControl?: string;
    upsert?: boolean;
  }
) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: options?.cacheControl || '3600',
      upsert: options?.upsert || false,
    });

  if (error) throw error;
  return data;
}

// Get public URL for a file
export function getPublicUrl(bucket: string, path: string) {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

// Delete file from storage
export async function deleteFile(bucket: string, path: string) {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
}

// List files in a bucket
export async function listFiles(bucket: string, folder?: string) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .list(folder || '', {
      limit: 100,
      offset: 0,
      sortBy: { column: 'name', order: 'asc' },
    });

  if (error) throw error;
  return data || [];
}

// Recursively list all files in a bucket (including subfolders)
export async function listAllFilesRecursive(bucket: string, folder = '', allFiles: any[] = []): Promise<any[]> {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(folder, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' },
      });

    if (error) {
      // If folder doesn't exist, that's okay - just return what we have
      if (error.message && error.message.includes('not found')) {
        return allFiles;
      }
      console.error('Error listing files in folder:', folder, error);
      return allFiles;
    }

    if (!data || data.length === 0) return allFiles;

    for (const item of data) {
      const fullPath = folder ? `${folder}/${item.name}` : item.name;
      
      // In Supabase Storage v2 API:
      // - Files have metadata with mimetype and size
      // - Folders have null or empty metadata
      // Also check if item.id exists (files have id, folders might not in some versions)
      const metadata = item.metadata as any;
      const hasMetadata = metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0;
      const hasMimetype = hasMetadata && metadata.mimetype;
      const isFolder = !hasMimetype && (!item.id || (metadata === null || (typeof metadata === 'object' && Object.keys(metadata).length === 0)));
      
      if (isFolder && item.name) {
        // It's a folder, recurse into it (but skip hidden/system folders)
        if (!item.name.startsWith('.') && !item.name.startsWith('_')) {
          await listAllFilesRecursive(bucket, fullPath, allFiles);
        }
      } else if (hasMimetype || item.id) {
        // It's a file - has either mimetype metadata or an id
        allFiles.push({
          ...item,
          fullPath,
        });
      }
    }

    return allFiles;
  } catch (err: any) {
    console.error('Error in recursive file listing:', err);
    return allFiles;
  }
}

// Upload logo
export async function uploadLogo(file: File, type: 'header' | 'footer' | 'site' = 'site') {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const path = `${type}/${timestamp}.${extension}`;
  
  return uploadFile(STORAGE_BUCKETS.LOGOS, path, file, {
    cacheControl: '31536000', // 1 year
    upsert: false,
  });
}

// Upload favicon
export async function uploadFavicon(file: File) {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const path = `favicon-${timestamp}.${extension}`;
  
  return uploadFile(STORAGE_BUCKETS.FAVICONS, path, file, {
    cacheControl: '31536000', // 1 year
    upsert: false,
  });
}

// Upload OG image
export async function uploadOGImage(file: File, landingId?: number) {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const path = landingId 
    ? `landing-${landingId}-${timestamp}.${extension}`
    : `og-${timestamp}.${extension}`;
  
  return uploadFile(STORAGE_BUCKETS.OG_IMAGES, path, file, {
    cacheControl: '31536000', // 1 year
    upsert: false,
  });
}

// Upload project image
export async function uploadProjectImage(file: File, projectId?: number) {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const path = projectId 
    ? `projects/${projectId}/${timestamp}.${extension}`
    : `projects/general/${timestamp}.${extension}`;
  
  const uploadResult = await uploadFile(STORAGE_BUCKETS.LANDING_ASSETS, path, file, {
    cacheControl: '31536000', // 1 year
    upsert: false,
  });
  
  // Return public URL
  return getPublicUrl(STORAGE_BUCKETS.LANDING_ASSETS, uploadResult.path);
}

// Upload website image (for hero backgrounds, service images, etc.)
export async function uploadWebsiteImage(file: File, type: 'hero' | 'service' | 'background' = 'background') {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const path = `website/${type}/${timestamp}.${extension}`;
  
  const uploadResult = await uploadFile(STORAGE_BUCKETS.LANDING_ASSETS, path, file, {
    cacheControl: '31536000', // 1 year
    upsert: false,
  });
  
  // Return public URL
  return getPublicUrl(STORAGE_BUCKETS.LANDING_ASSETS, uploadResult.path);
}

// List website images from bucket
export async function listWebsiteImages(bucket: string = STORAGE_BUCKETS.LANDING_ASSETS, folder?: string) {
  try {
    const searchFolder = folder || 'website';
    const allFiles = await listAllFilesRecursive(bucket, searchFolder);
    
    // Filter only image files
    const imageFiles = allFiles.filter((file: any) => {
      if (!file.id) return false;
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
    });
    
    // Map to our format with public URLs
    return imageFiles.map((file: any) => ({
      name: file.name,
      path: file.fullPath,
      url: getPublicUrl(bucket, file.fullPath),
      createdAt: file.created_at,
      updatedAt: file.updated_at,
    }));
  } catch (error: any) {
    console.error('Error listing website images:', error);
    return [];
  }
}

// List logos from logos bucket
export async function listLogos() {
  return listWebsiteImages(STORAGE_BUCKETS.LOGOS);
}

// List project images from bucket (recursively searches all folders)
export async function listProjectImages() {
  try {
    // List all files recursively starting from root
    const allFiles = await listAllFilesRecursive(STORAGE_BUCKETS.LANDING_ASSETS, '');
    
    // Filter only image files
    const imageFiles = allFiles.filter((file: any) => {
      // Check if it has an id (is a file, not a folder)
      if (!file.id) return false;
      
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
    });
    
    // Map to our format with public URLs
    return imageFiles.map((file: any) => ({
      name: file.name,
      path: file.fullPath,
      url: getPublicUrl(STORAGE_BUCKETS.LANDING_ASSETS, file.fullPath),
      createdAt: file.created_at,
      updatedAt: file.updated_at,
    }));
  } catch (error: any) {
    console.error('Error listing project images:', error);
    // Fallback: try listing from projects folder
    try {
      const files = await listFiles(STORAGE_BUCKETS.LANDING_ASSETS, 'projects');
      const imageFiles = files.filter((file) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
      });
      
      return imageFiles.map((file) => ({
        name: file.name,
        path: `projects/${file.name}`,
        url: getPublicUrl(STORAGE_BUCKETS.LANDING_ASSETS, `projects/${file.name}`),
        createdAt: file.created_at,
        updatedAt: file.updated_at,
      }));
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      return [];
    }
  }
}

